import { LoginActionType } from "models/actionTypes/loginActionTypes";
import { ReduxConstants } from "utils/constants";
import { RootActionType } from "models/actionTypes/rootActionTypes";
import { AuthState, BusinessUnitManagerSettings, Person } from "models/stateTypes/authState";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DateHelper } from "utils/helpers";

const initialState: AuthState = {
  loggedInUser: new Person(),
  isAuthorized: false,
  selectedCompany: {} as WintSuperkollAPIModel.Company,
  selectedCompanyId: undefined,
  fetchingRefreshToken: false,
  profileToggles: {} as WintSuperkollAPIModel.ProfileToggles,
  canPayEmployee: undefined,
  businessUnitManagerSettings: new BusinessUnitManagerSettings(),
};

export const authReducer = (state: AuthState = initialState, action: LoginActionType | RootActionType): AuthState => {
  switch (action.type) {
    case ReduxConstants.RootActions.RESET:
      return initialState;
    case ReduxConstants.AuthActions.LOGIN_SUCCESS:
      return {
        ...state,
        loggedInUser: action.payload.user,
        isAuthorized: true,
        companies: undefined,
      };
    case ReduxConstants.AuthActions.LOGIN_ERROR:
    case ReduxConstants.AuthActions.LOGOUT:
      return { ...initialState };
    case ReduxConstants.AuthActions.LOAD_COMPANIES:
      return { ...state, companies: action.payload };
    case ReduxConstants.AuthActions.LOAD_COMPANY_DATA:
      return {
        ...state,
        selectedCompany: {
          ...action.payload,
          lockDate: action.payload.lockDate ? DateHelper.resetTime(new Date(action.payload.lockDate)) : undefined,
        },
      };
    case ReduxConstants.AuthActions.SET_SELECTED_COMPANY:
      return {
        ...state,
        selectedCompany: {
          ...action.payload.company,
          lockDate: action.payload.company.lockDate
            ? DateHelper.resetTime(new Date(action.payload.company.lockDate))
            : undefined,
        },
      };
    case ReduxConstants.AuthActions.SET_PROFILE_PICTURE:
      return {
        ...state,
        loggedInUser: { ...state.loggedInUser, profileImage: action.payload },
      };
    case ReduxConstants.AuthActions.ADD_PAYMENT_METHOD:
      return {
        ...state,
        loggedInUser: { ...state.loggedInUser, paymentMethods: [...state.loggedInUser.paymentMethods, action.payload] },
      };
    case ReduxConstants.AuthActions.UPDATE_PAYMENT_METHOD:
      const methods = state.loggedInUser.paymentMethods.map((method) =>
        method.id === action.payload.id ? action.payload : method
      );
      return {
        ...state,
        loggedInUser: { ...state.loggedInUser, paymentMethods: [...methods] },
      };
    case ReduxConstants.AuthActions.LOAD_PAYMENT_METHODS:
      return {
        ...state,
        loggedInUser: {
          ...state.loggedInUser,
          paymentMethods: action.payload.paymentMethods,
          wintCardPaymentMethods: action.payload.wintCardPaymentMethods,
        },
      };
    case ReduxConstants.AuthActions.LOAD_PROFILE_TOGGLES:
      return {
        ...state,
        profileToggles: action.payload,
      };
    case ReduxConstants.AuthActions.HIDE_EULA:
      return {
        ...state,
        loggedInUser: { ...state.loggedInUser, unsignedEula: false },
      };
    case ReduxConstants.AuthActions.SET_PROFILE_NAME:
      return {
        ...state,
        loggedInUser: { ...state.loggedInUser, name: action.payload },
      };
    case ReduxConstants.AuthActions.SET_FETCHING_REFRESH_TOKEN:
      return { ...state, fetchingRefreshToken: action.payload };
    case ReduxConstants.AuthActions.SET_AUTH_TOKENS:
      return {
        ...state,
        fetchingRefreshToken: false,
        loggedInUser: { ...state.loggedInUser, authTokens: action.payload },
      };
    case ReduxConstants.AuthActions.ADD_INTRODUCTION:
      return {
        ...state,
        profileToggles: {
          ...state.profileToggles,
          shownIntroductions: [...(state.profileToggles.shownIntroductions ?? []), action.payload],
        },
      };
    case ReduxConstants.AuthActions.ADD_HELP:
      return {
        ...state,
        profileToggles: {
          ...state.profileToggles,
          shownHelps: [...(state.profileToggles.shownHelps ?? []), action.payload],
        },
      };
    case ReduxConstants.AuthActions.SET_SELECTED_COMPANY_ID:
      return {
        ...state,
        selectedCompanyId: action.payload,
      };
    case ReduxConstants.AuthActions.SET_HAS_MISSING_PAYMENT_DETAILS:
      return {
        ...state,
        loggedInUser: { ...state.loggedInUser, hasMissingPaymentDetails: action.payload },
      };
    case ReduxConstants.AuthActions.SET_CAN_PAY_EMPLOYEE:
      return {
        ...state,
        canPayEmployee: action.payload,
      };
    case ReduxConstants.AuthActions.SET_SHOW_ALL_COMPANIES_TODO_OVERVIEW:
      return {
        ...state,
        showAllCompaniesTodoOverview: action.payload,
      };
    case ReduxConstants.AuthActions.LOAD_BUSINESS_UNIT_MANAGER_SETTINGS:
    case ReduxConstants.AuthActions.UPDATE_BUSINESS_UNIT_MANAGER_SETTINGS:
      return {
        ...state,
        businessUnitManagerSettings: action.payload,
      };
    case ReduxConstants.AuthActions.LOAD_TAX_AGENT_OF_COMPANY:
      return {
        ...state,
        taxAgent: action.payload,
      };
    case ReduxConstants.AuthActions.HIDE_WINTCARD_PROMOTION:
      return {
        ...state,
        selectedCompany: {
          ...state.selectedCompany,
          allowWintCardRegistration: false,
        },
      };
    case ReduxConstants.AuthActions.UPDATE_INVOICE_ACTIVATION_STATE:
      return {
        ...state,
        selectedCompany: {
          ...state.selectedCompany,
          invoiceActivationState: action.payload,
          invoiceCreator:
            action.payload === WintSuperkollAPIModel.InvoiceActivationState.Activated
              ? state.selectedCompany.invoiceUser
              : state.selectedCompany.invoiceCreator,
        },
      };
    default:
      return state;
  }
};
